import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HorizontalDivider } from '@sorare/core/src/atoms/ui/Divider';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import {
  type LeagueLaunch as LeagueLaunchType,
  useLeagueLaunches,
} from '@sorare/marketplace/src/lib/newSeason';

import { Calendar } from '#src/components/new-season/Calendar/index.tsx';
import { LeagueLaunch } from '#src/components/new-season/LeagueLaunch/index.tsx';
import { MainLeagueLaunch } from '#src/components/new-season/MainLeagueLaunch/index.tsx';
import { NewSeasonHeader } from '#src/components/new-season/NewSeasonHeader/index.tsx';
import { ReleasedLeagues } from '#src/components/new-season/ReleasedLeagues/index.tsx';
import { SelectNewSeasonRarity } from '#src/components/new-season/SelectNewSeasonRarity/index.tsx';

import type {
  NewSeasonPageQuery,
  NewSeasonPageQueryVariables,
} from './__generated__/index.graphql';
import { useLeagueScrollDetection } from './useLeagueScrollDetection';
import { useSortLeagueLaunches } from './useSortLeagueLaunches';

const Root = styled(Vertical).attrs({ gap: 0 })`
  container-type: inline-size;
  isolation: isolate;
  padding-bottom: 300px;
`;

const MarketRoot = styled.div`
  background-color: var(--c-black);
  color: var(--c-white);
  isolation: isolate;
`;

const Container = styled(Vertical).attrs({ gap: 5 })`
  padding-bottom: var(--triple-unit);
`;

const Group = styled(Vertical).attrs({ gap: 2, center: true })`
  width: 100%;
  @container ${tabletAndAbove} {
    align-items: flex-start;
    flex-direction: row;
  }
`;

const GroupLeagues = styled(Vertical)`
  width: 100%;
  @container ${tabletAndAbove} {
    width: calc(100% - calc(var(--unit) * 11));
  }
`;

const OrderCalendar = styled(Calendar)`
  order: -1;
`;

const NEW_SEASON_PAGE_QUERY = gql`
  query NewSeasonPageQuery($leagues: [String!]!) {
    football {
      competitions(slugs: $leagues) {
        slug
        ...NewSeasonHeader_competition
        ...LeagueLaunch_competition
        ...MainLeagueLaunch_competition
        ...ReleasedLeagues_competition
      }
    }
    so5 {
      so5Competitions(sport: FOOTBALL, eventType: CLASSIC) {
        slug
        competitions {
          slug
        }
        scoutable
        ...NewSeasonHeader_so5Competition
        ...LeagueLaunch_so5Competition
        ...MainLeagueLaunch_so5Competition
      }
    }
  }
  ${NewSeasonHeader.fragments.competition}
  ${NewSeasonHeader.fragments.so5Competition}
  ${LeagueLaunch.fragments.competition}
  ${LeagueLaunch.fragments.so5Competition}
  ${ReleasedLeagues.fragments.competition}
  ${MainLeagueLaunch.fragments.competition}
  ${MainLeagueLaunch.fragments.so5Competition}
` as TypedDocumentNode<NewSeasonPageQuery, NewSeasonPageQueryVariables>;

type Competition = NewSeasonPageQuery['football']['competitions'][number];
type So5Competition = NewSeasonPageQuery['so5']['so5Competitions'][number];

export const NewSeasonPage = () => {
  const leagueLaunches = useLeagueLaunches();

  const { data } = useQuery(NEW_SEASON_PAGE_QUERY, {
    variables: {
      leagues: leagueLaunches.map(l => l.slug),
    },
  });

  const competitions = data?.football.competitions;

  const leagues: LeagueLaunchType<Competition, So5Competition>[] =
    leagueLaunches.map(l => ({
      ...l,
      competition: competitions?.find(c => c.slug === l.slug),
      so5Competition: data?.so5.so5Competitions?.find(
        so5Competition =>
          so5Competition.competitions.some(c => c.slug === l.slug) &&
          so5Competition.scoutable
      ),
    }));

  const {
    releasedLeagueLaunches: releasedLeagueLaunchesRaw,
    liveAndUpcomingLeagueLaunches,
    liveAndUpcomingLeagueLaunchesDates,
    groupedByDateLiveAndUpcomingLeagueLaunches,
  } = useSortLeagueLaunches({
    leagues,
  });

  const releasedLeagueLaunches = releasedLeagueLaunchesRaw
    .reduce(
      (acc, l) => {
        if (l.launchType === 'MAIN_LEAGUE') {
          acc[0].push(l);
        } else if (l.launchType === 'SECONDARY_LEAGUE') {
          if ((l.so5Competition?.competitions.length || 0) === 1) {
            acc[1].push(l);
          } else if ((l.so5Competition?.competitions.length || 0) > 1) {
            acc[2].push(l);
          }
        }
        return acc;
      },
      [[], [], []] as LeagueLaunchType<Competition, So5Competition>[][]
    )
    .flat();

  const { visibleLeague, leagueRefs, headerHeight, registerLeagueRef } =
    useLeagueScrollDetection(leagues);

  const [selectedLeague, setSelectedLeague] = useState<string>(
    leagues?.[0]?.slug
  );

  // Update selected league when scroll changes
  useEffect(() => {
    if (visibleLeague && visibleLeague !== selectedLeague) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- mirror scroll-spy into highlight state
      setSelectedLeague(visibleLeague);
    }
  }, [visibleLeague, selectedLeague]);

  const handleLeagueChange = (leagueSlug: string) => {
    setSelectedLeague(leagueSlug);
    const element = leagueRefs.current.get(leagueSlug);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      });
    }
  };

  const featuredMainLeagueLaunch =
    (liveAndUpcomingLeagueLaunches?.length === 1 &&
      liveAndUpcomingLeagueLaunches?.[0].launchType === 'MAIN_LEAGUE' &&
      liveAndUpcomingLeagueLaunches?.[0]) ||
    null;

  return (
    <Root gap={0}>
      <NewSeasonHeader
        featured={featuredMainLeagueLaunch}
        selectedLeague={selectedLeague}
        handleLeagueChange={handleLeagueChange}
        upcomingAndLiveLeagues={liveAndUpcomingLeagueLaunches}
        releasedLeagues={releasedLeagueLaunches}
      />
      <MarketRoot>
        <Container gap={5}>
          <Vertical gap={3}>
            {(featuredMainLeagueLaunch
              ? liveAndUpcomingLeagueLaunchesDates.slice(1)
              : liveAndUpcomingLeagueLaunchesDates
            ).map(d => (
              <Group key={d}>
                <GroupLeagues>
                  {groupedByDateLiveAndUpcomingLeagueLaunches[d]?.map(
                    league => (
                      <div
                        key={league.slug}
                        ref={element => registerLeagueRef(league.slug, element)}
                        style={{
                          scrollMarginTop: `${headerHeight}px`,
                        }}
                      >
                        {league.launchType === 'MAIN_LEAGUE' ? (
                          <MainLeagueLaunch league={league} />
                        ) : (
                          <LeagueLaunch leagueLaunch={league} />
                        )}
                      </div>
                    )
                  )}
                </GroupLeagues>
                <OrderCalendar
                  date={new Date(d)}
                  sticky={
                    (groupedByDateLiveAndUpcomingLeagueLaunches[d]?.length ||
                      0) > 1 ||
                    (groupedByDateLiveAndUpcomingLeagueLaunches[d]?.length ===
                      1 &&
                      groupedByDateLiveAndUpcomingLeagueLaunches[d][0]
                        .launchType === 'MAIN_LEAGUE')
                  }
                />
              </Group>
            ))}
          </Vertical>
          {releasedLeagueLaunches.length > 0 && (
            <>
              <HorizontalDivider />
              <ReleasedLeagues
                releasedLeagues={releasedLeagueLaunches}
                registerLeagueRef={registerLeagueRef}
              />
            </>
          )}
        </Container>
      </MarketRoot>
      <SelectNewSeasonRarity />
    </Root>
  );
};
